import React, { useState, useEffect, useRef } from 'react'
import {Check, X, Pencil, Loader2} from 'lucide-react'
import { useSiteEdit } from '@/contexts/SiteEditContext'

interface EditableTextProps {
  /** Cle unique du bloc de contenu (ex: hero.title) */
  contentKey: string
  /** Contenu d'origine (fallback si rien en base) */
  children: string
  /** Section pour regroupement dans l'editeur */
  section?: string
  /** Libelle lisible dans l'editeur admin */
  label?: string
  /** Champ multiligne (textarea) */
  multiline?: boolean
  /** Element a rendre autour du texte (ex: 'h1', 'p', 'span') */
  as?: keyof JSX.IntrinsicElements
  className?: string
}

/**
 * Bloc de texte editable en direct.
 * - Visiteurs / non-edit : rend simplement le texte (base ou fallback).
 * - Admin + mode edition : surlignage + clic pour editer inline.
 */
const EditableText: React.FC<EditableTextProps> = ({
  contentKey,
  children,
  section,
  label,
  multiline = false,
  as = 'span',
  className = '',
}) => {
  const { get, save, editMode, canEdit, saving } = useSiteEdit()
  const value = get(contentKey, children)

  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  const inputRef = useRef<HTMLTextAreaElement | HTMLInputElement | null>(null)

  useEffect(() => {
    if (!editing) setDraft(value)
  }, [value, editing])

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select?.()
    }
  }, [editing])

  const Tag = as as React.ElementType
  const active = canEdit && editMode

  const handleSave = async () => {
    if (draft === value) {
      setEditing(false)
      return
    }
    try {
      await save(contentKey, draft, {
        type: multiline ? 'richtext' : 'text',
        section,
        label: label || contentKey,
      })
      setEditing(false)
    } catch {
      /* toast gere dans le contexte */
    }
  }

  const handleCancel = () => {
    setDraft(value)
    setEditing(false)
  }

  // Mode edition actif + en cours d'edition -> editeur inline
  if (active && editing) {
    return (
      <span className="relative inline-block w-full align-top">
        {multiline ? (
          <textarea
            ref={inputRef as React.RefObject<HTMLTextAreaElement>}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={4}
            className="w-full bg-[#0A1525] text-inherit font-inherit border-2 border-[#A3C644] rounded-lg px-3 py-2 outline-none resize-y leading-snug"
          />
        ) : (
          <input
            ref={inputRef as React.RefObject<HTMLInputElement>}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSave()
              if (e.key === 'Escape') handleCancel()
            }}
            className="w-full bg-[#0A1525] text-inherit font-inherit border-2 border-[#A3C644] rounded-lg px-3 py-1.5 outline-none"
          />
        )}
        <span className="mt-2 flex items-center gap-2">
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#A3C644] text-[#0E1B2C] text-xs font-semibold rounded-md hover:bg-[#b4d655] transition-colors disabled:opacity-60"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
            Enregistrer
          </button>
          <button
            onClick={handleCancel}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#E8DCC4]/30 text-[#E8DCC4]/70 text-xs rounded-md hover:border-[#E8DCC4]/60 transition-colors"
          >
            <X className="w-3.5 h-3.5" />
            Annuler
          </button>
        </span>
      </span>
    )
  }

  // Mode edition actif (mais pas en train d'editer) -> surlignage cliquable
  if (active) {
    return (
      <Tag
        onClick={() => {
          setDraft(value)
          setEditing(true)
        }}
        className={`${className} group relative cursor-pointer rounded-md transition-all outline-dashed outline-1 outline-[#A3C644]/40 hover:outline-[#A3C644] hover:bg-[#A3C644]/5`}
        title="Cliquer pour modifier"
      >
        {value}
        <span className="pointer-events-none absolute -top-2 -right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-[#A3C644] text-[#0E1B2C] rounded-full p-1 shadow-lg">
          <Pencil className="w-3 h-3" />
        </span>
      </Tag>
    )
  }

  // Rendu normal (visiteur)
  return <Tag className={className}>{value}</Tag>
}

export default EditableText
